import {Accordion, AccordionDetails, AccordionSummary, Card, Grid, Typography} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';

const Others = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={'others-content'} id={'others-header'}>
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Other</div>
                    <div>{counter('others')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'others',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.VentricularTachycardia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Ventricular Tachycardia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={
                                                        parameters.ventriculartachycardiaDiagnosisDate?.value || null
                                                    }
                                                    onChange={(date) =>
                                                        valueChanged(
                                                            parameters.ventriculartachycardiaDiagnosisDate,
                                                            date,
                                                        )
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ventriculartachycardiaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.ventriculartachycardiaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.ventriculartachycardiaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ventriculartachycardiaSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.ventriculartachycardiaSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ventriculartachycardiaAblation',
                                                    label: 'Have you had ablation?',
                                                    ...parameters.ventriculartachycardiaAblation,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.VentricularFibrillation.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Ventricular Fibrillation</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.vFibDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.vFibDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="vFibTreatment"
                                                label="What treatment was prescribed or completed for this condition? Provide details of your treatment."
                                                value={parameters.vFibTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.vFibTreatment, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="Provide details to include the type of surgery and date it was completed."
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.vFibSurgeryDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.vFibSurgeryDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="vFibSymptoms"
                                                label="Are you currently experiencing symptoms related to this condition? Provide details of your symptoms."
                                                value={parameters.vFibSymptoms?.value}
                                                onChange={(value) => valueChanged(parameters.vFibSymptoms, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="Provide date ablation was completed."
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.vFibAblationDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.vFibAblationDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.AtrialFibulationAfib.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Atrial Fibrillation (Afib)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.afibDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.afibDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="afibAblation"
                                                label="What treatment was prescribed, did you have ablation?"
                                                value={parameters.afibAblation?.value}
                                                onChange={(value) => valueChanged(parameters.afibAblation, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'afibResolved',
                                                    label: 'is AFIB resolved or still present? (unresolved is chronic)',
                                                    ...parameters.afibResolved,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="afibEpisodes"
                                                label="How many episodes have you had in the last year?"
                                                value={parameters.afibEpisodes?.value}
                                                onChange={(value) => valueChanged(parameters.afibEpisodes, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'afibHeartDisease',
                                                    label: 'Have You been diagnosed with heart disease',
                                                    ...parameters.afibHeartDisease,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.BloodClotDVTDeepVeinThrombosis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Blood Clot (DVT - Deep Vein Thrombosis)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.dvtDiagnosisDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.dvtDiagnosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'dvtResolved',
                                                    label: "Do you currently have any DVT's?",
                                                    ...parameters.dvtResolved,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'dvtEpisodes',
                                                    label: 'Have you had more than one episode of this condition?',
                                                    ...parameters.dvtEpisodes,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="dvtLastEpisode"
                                                label="When was your last episode?"
                                                value={parameters.dvtLastEpisode?.value}
                                                onChange={(value) => valueChanged(parameters.dvtLastEpisode, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="dvtTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.dvtTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.dvtTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Psoriasis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Psoriasis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.psoriasisDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.psoriasisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'psoriasisPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital?',
                                                    ...parameters.psoriasisPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'psoriasisOralMedications',
                                                    label: 'Are you taking any oral or injectable medication (i.e. Cyclosporine, Methotrexate, Enbrel, Humira, etc)?',
                                                    ...parameters.psoriasisOralMedications,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="psoriasisTreatmentPlan"
                                                label="Provide details on treatment received for this condition. Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.psoriasisTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.psoriasisTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.AnginaChestPain.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Angina / Chest Pain</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.anginaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.anginaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="anginaEvaluation"
                                                label="Provide details on the type of evaluation and/or testing that was completed for diagnosis."
                                                value={parameters.anginaEvaluation?.value}
                                                onChange={(value) => valueChanged(parameters.anginaEvaluation, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="anginaTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.anginaTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.anginaTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.AVBlock.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">AV Block</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.avblockDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.avblockDiagnosisDate, date)
                                                    }
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="avblockTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.avblockTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.avblockTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'avblockAblation',
                                                    label: 'Have you had ablation',
                                                    ...parameters.avblockAblation,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'avblockSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.avblockSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default Others;
